import { useState } from "react";
import { connect, mapProps, useField } from "@formily/react";
import { useStateMachine } from "little-state-machine";
import { Button, IRumbleFormScope } from ".";
import { Card } from "antd";

/**
 * RumbleImageUploadField component wraps RumbleImageUpload to work with Formily
 * This component uploads images and integrates with Formily forms
 */
const ImageUploadField = (props: any) => {
  const field = useField();
  const { state } = useStateMachine();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const scope = (state as { scope?: IRumbleFormScope }).scope;
  const layout = props.layout || "vertical";

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    setIsLoading(true);
    setError(null);

    try {
      // Create a temporary preview for immediate feedback
      if (file.type.startsWith("image/")) {
        const objectUrl = URL.createObjectURL(file);
        setPreviewUrl(objectUrl);
      }

      if (scope?.uploadMedia) {
        // Use the uploadMedia function from the scope
        const response = await scope.uploadMedia({
          file,
          onSuccess: (response) => {
            props.onChange(response);
          },
          onError: (error) => {
            setError(error.message || "Failed to upload image");
          },
        });

        // Set the uploaded file data as the field value if not already set by onSuccess
        if (response) {
          props.onChange(response);
        }
      } else {
        // If no upload function is available, simply set the file as value
        props.onChange(file);
        console.warn(
          "No upload function available, setting file object as value",
        );
      }
    } catch (err) {
      console.error("Failed to upload image:", err);
      setError(err instanceof Error ? err.message : "Failed to upload image");
    } finally {
      setIsLoading(false);
    }
  };

  // Determine the image source to display
  const imageSource = field?.value
    ? field.value.contentUrl
    : previewUrl
      ? previewUrl
      : props.contentUrl
        ? props.contentUrl
        : `${props.cdnUrl || ""}/static/widget/form/camera.png`;

  // Determine which button text to show
  const buttonText = isLoading
    ? "Uploading..."
    : props.value?.contentUrl || previewUrl
      ? "Vervang foto"
      : "Upload";

  if (layout === "horizontal") {
    return (
      <label htmlFor={props.name || "image-upload"} className="cursor-pointer">
        <Card className="mb-4">
          <div className="pl-3 pr-5 py-5 w-full flex items-center">
            <div className="h-full pr-3 min-w-40 w-40">
              <img
                className="object-contain w-full h-full"
                src={imageSource}
                alt="Afbeelding uploaden"
              />
            </div>

            <div className="flex flex-col justify-center flex-1">
              <p className="font-semibold leading-6 text-lg  text-ellipsis ">
                {field.title || "Upload Image"}
              </p>

              <div className="py-5 my-auto">
                {field.description ? <p>{field.description}</p> : null}
                {error ? <p className="text-red-500 mt-2">{error}</p> : null}
              </div>

              <div className="mt-auto">
                <Button
                  loading={isLoading}
                  htmlType="button"
                  className={`w-full pointer-events-none ${
                    error
                      ? "bg-red-500 text-white"
                      : field?.value?.contentUrl || previewUrl
                        ? "bg-green-500 text-white"
                        : ""
                  }`}
                >
                  {buttonText}
                </Button>

                <input
                  id={props.name || "image-upload"}
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files?.[0])}
                  type="file"
                  accept={props.accept || "image/*,application/pdf"}
                />
              </div>
            </div>
          </div>
        </Card>
        {props.errors && props.errors.length > 0 ? (
          <div className="text-red-500 p-4">
            {props.errors.map((err: any, index: number) => (
              <div key={index}>
                {err.messages ? err.messages.join(", ") : "Error"}
              </div>
            ))}
          </div>
        ) : null}
      </label>
    );
  }

  return (
    <label
      htmlFor={props.name || "image-upload"}
      className="cursor-pointer block"
    >
      <div className="border border-neutral-300 rounded-lg p-5 w-full h-full flex flex-col items-center">
        <div className="h-full pr-3 min-w-40 w-full relative mb-4">
          <img
            className="object-contain w-full h-full"
            src={imageSource}
            alt="Afbeelding uploaden"
          />
          {isLoading ? (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <div className="text-white">Uploading...</div>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col justify-center flex-1">
          <p className="font-semibold leading-6 text-lg text-ellipsis">
            {field.title || "Upload Image"}
          </p>

          <div className="py-3 my-auto text-neutral-500 font-medium leading-5">
            {field.description ? <p>{field.description}</p> : null}
            {error ? <p className="text-red-500 mt-2">{error}</p> : null}
          </div>

          <div className="mt-auto">
            <Button
              loading={isLoading}
              htmlType="button"
              className={`w-full pointer-events-none ${
                error
                  ? "bg-red-500 text-white"
                  : field?.value?.contentUrl || previewUrl
                    ? "bg-green-500 text-white"
                    : ""
              }`}
            >
              {buttonText}
            </Button>

            {props.errors && props.errors.length > 0 ? (
              <div className="text-red-500 p-4">
                {props.errors.map((err: any, index: number) => (
                  <div key={index}>
                    {err.messages ? err.messages.join(", ") : "Error"}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <input
            id={props.name || "image-upload"}
            className="hidden"
            onChange={(e) => handleFileUpload(e.target.files?.[0])}
            type="file"
            accept={props.accept || "image/*,application/pdf"}
          />
        </div>
      </div>
    </label>
  );
};

// Connect the component to Formily
export const RumbleImageUpload = connect(
  ImageUploadField,
  mapProps((props: any, field: any) => {
    return {
      ...props,
      value: props.value,
      onChange: props.onChange,
      invalid: field?.valid === false,
      errors: field?.errors,
      name: field?.props?.name,
    };
  }),
);
